import { useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { getClassificationSummary } from "@utils/classification";
import { getReferralPartnerDetails } from "@endpoints/candidate.service";
import { updateReferralAccountApi } from "@endpoints/referral.service";

type ReferralPartnerSummary = {
  _id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  profilePic?: string;
  refNo?: string;
  placements?: number;
  totalApplied?: number;
  topPlacedCount?: number;
  topTotalApplications?: number;
  successPercentage?: number;
  successRateBasedOnCandidateStatus?: number;
  badge?: string | null;
  topClassification?: {
    main?: string;
    sub?: string[];
  } | null;
  averageResponseTime?: {
    value?: number;
    unit?: "minutes" | "hours";
    label?: string;
    responseCount?: number;
  } | null;
  referralScore?: number | null;
};

type ReferralPartnerDetails = ReferralPartnerSummary & {
  rating?: number | null;
  candidateSatisfactionPercentage?: number | null;
  totalInvitedCandidates?: number;
  totalAppliedCandidates?: number;
  totalPlacedCandidates?: number;
  bestSuccessArea?: {
    classification?: {
      main?: string;
      sub?: string[];
    } | null;
    placements?: number;
    totalApplications?: number;
    successPercentage?: number;
    successRateBasedOnCandidateStatus?: number;
    totalAppliedCandidates?: number;
    placedCandidates?: number;
    totalSupportedCandidates?: number;
    placedSupportedCandidates?: number;
  } | null;
  averageResponseTime?: {
    value?: number;
    unit?: "minutes" | "hours";
    label?: string;
    responseCount?: number;
  } | null;
};

type Props = {
  open: boolean;
  referralId?: string | null;
  referralPartner?: ReferralPartnerSummary | null;
  onClose: () => void;
  onSelectPartner?: (partner: ReferralPartnerDetails) => void;
};

const formatResponseTime = (value?: number, unit?: "minutes" | "hours") => {
  if (!value || value <= 0) return "N/A";
  const rounded = Number(value.toFixed(2));
  return unit === "hours"
    ? `${rounded}${Number.isInteger(rounded) ? "" : ""}h avg`
    : `${rounded}${Number.isInteger(rounded) ? "" : ""}m avg`;
};

const ReferralPartnerDetailsPopup = ({
  open,
  referralId,
  referralPartner,
  onClose,
  onSelectPartner,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [details, setDetails] = useState<ReferralPartnerDetails | null>(null);

  useEffect(() => {
    if (!open || !referralId) return;

    let cancelled = false;
    setLoading(true);
    setDetails(null);

    getReferralPartnerDetails(referralId)
      .then((response) => {
        if (cancelled) return;
        const payload =
          response?.data?.data ?? response?.data ?? response?.data?.referralPartner;
        setDetails(payload || null);
      })
      .catch((error: any) => {
        if (cancelled) return;
        enqueueSnackbar(
          error?.message || "Failed to load referral partner details",
          { variant: "error" }
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enqueueSnackbar, open, referralId]);

  const partner = details || referralPartner || null;
  const fullName = `${partner?.firstName || ""} ${partner?.lastName || ""}`.trim();
  const successRate = Number(
    (partner?.successPercentage ?? 0).toFixed(2)
  );
  const placements = Number(
    partner?.placements ?? partner?.topPlacedCount ?? 0
  );
  const candidateSatisfaction = Number(
    (details?.candidateSatisfactionPercentage ?? 0).toFixed(2)
  );
  const responseTimeValue =
    details?.averageResponseTime?.label ||
    formatResponseTime(
      details?.averageResponseTime?.value,
      details?.averageResponseTime?.unit
    );
  const bestSuccessArea = useMemo(() => {
    const area =
      details?.bestSuccessArea?.classification ||
      partner?.topClassification ||
      null;
    const label = getClassificationSummary(area || undefined);
    return placements > 0 && label !== "-" ? label : "-";
  }, [details?.bestSuccessArea?.classification, partner?.topClassification, placements]);

  const tag = useMemo(() => {
    const apiBadge = details?.badge ?? referralPartner?.badge ?? null;
    if (apiBadge) return apiBadge;

    if (placements > 0 && successRate >= 75) return "Top Performer";
    if (responseTimeValue !== "N/A" && (details?.averageResponseTime?.value || 0) <= 2)
      return "Fast Responder";
    if (candidateSatisfaction >= 80) return "High Satisfaction";
    return null;
  }, [candidateSatisfaction, details?.averageResponseTime?.value, details?.badge, placements, referralPartner?.badge, responseTimeValue, successRate]);

  const whyTheyFitText = useMemo(() => {
    if (bestSuccessArea === "-" || placements <= 0) {
      return "This referral partner is still building placement history, so we do not have enough data to generate a strong match insight yet.";
    }
    return `Strong success rate in ${bestSuccessArea}, with recent placements aligned to this area.`;
  }, [bestSuccessArea, placements]);

  const handleSelectAsPartner = async () => {
    const email = partner?.email;
    if (!email) {
      enqueueSnackbar("Referral partner email is missing.", {
        variant: "error",
      });
      return;
    }

    try {
      setSubmitting(true);
      const { success } = await updateReferralAccountApi({
        referralEmail: email,
      });
      if (!success) {
        throw new Error("Unable to select referral partner");
      }
      enqueueSnackbar("Referral partner selected successfully.", {
        variant: "success",
      });
      onSelectPartner?.(details || (partner as ReferralPartnerDetails));
      onClose();
    } catch (error: any) {
      enqueueSnackbar(error?.message || "Unable to select referral partner", {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/55 px-4 py-6">
      <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl">
        <div className="border-b border-[#E6EBF2] px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EDF2FF] text-2xl font-semibold text-[#1D4ED8]">
                {partner?.profilePic ? (
                  <img
                    src={partner.profilePic}
                    alt={fullName || "Referral partner"}
                    className="h-full w-full rounded-2xl object-cover"
                  />
                ) : (
                  <span>{(partner?.firstName || "R").charAt(0)}</span>
                )}
              </div>

              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="truncate text-3xl font-semibold text-primary">
                    {fullName || "Referral partner"}
                  </h2>
                  {tag ? (
                    <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full bg-[#E6F8F0] px-3 py-1 text-xs font-medium text-[#0F9D58]">
                      {tag}
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 text-sm text-light-black">
                  Referral Partner Profile
                </p>
                <p className="mt-3 text-sm">
                  <span className="font-semibold text-primary">
                    Best success area:
                  </span>{" "}
                  <span className="font-semibold text-primary">
                    {bestSuccessArea}
                  </span>
                </p>
                {placements > 0 ? (
                  <p className="mt-1 text-sm text-[#8090A7]">
                    {placements} placements in {bestSuccessArea}
                  </p>
                ) : null}
              </div>
            </div>

            <button
              onClick={onClose}
              className="rounded-full bg-[#F4F6FA] p-3 text-lg leading-none text-primary transition hover:bg-[#EBEFF7]"
              aria-label="Close referral partner details"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 py-6">
          <div className="flex flex-wrap gap-3">
            <button className="rounded-full bg-[#2257FF] px-4 py-2 text-sm font-medium text-white transition">
              Overview
            </button>
            {[
              { key: "track-record", label: "Track Record" },
              { key: "reviews", label: "Reviews" },
              { key: "message", label: "Message" },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => undefined}
                className="rounded-full bg-[#F4F6FA] px-4 py-2 text-sm font-medium text-primary transition hover:bg-[#F0F4FA]"
              >
                {tab.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex min-h-[280px] items-center justify-center">
              <LoadingAnimation color={"black"} />
            </div>
          ) : (
            <div className="grid min-h-0 grid-cols-[1.8fr_1fr] gap-6 max-lg:grid-cols-1">
              <div className="rounded-[22px] border border-[#E2E8F0] bg-white p-6">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-primary">
                      How this referral partner can help
                    </h3>
                    <p className="mt-3 max-w-3xl text-sm leading-7 text-light-black">
                      {whyTheyFitText}
                    </p>
                  </div>

                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="rounded-[18px] border border-[#DDE9FF] bg-[#F3F7FF] p-5">
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#2257FF]">
                        Referral score
                      </p>
                      <p className="mt-2 text-[28px] font-semibold text-primary">
                        {partner?.referralScore ?? "—"}
                      </p>
                    </div>
                    <div className="rounded-[18px] border border-[#CFEFDD] bg-[#EFFBF4] p-5">
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#0F9D58]">
                        Candidate satisfaction
                      </p>
                      <p className="mt-2 text-[28px] font-semibold text-primary">
                        {details?.candidateSatisfactionPercentage != null
                          ? `${candidateSatisfaction}%`
                          : "—"}
                      </p>
                    </div>
                    <div className="rounded-[18px] border border-[#F8E8BE] bg-[#FFF8EA] p-5">
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#F28B20]">
                        Avg response
                      </p>
                      <p className="mt-2 text-[28px] font-semibold text-primary">
                        {responseTimeValue}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="rounded-[22px] border border-[#E2E8F0] bg-white p-6">
                  <h3 className="text-lg font-semibold text-primary">
                    Performance snapshot
                  </h3>

                  <div className="mt-5 space-y-4 text-sm">
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-light-black">Placements</span>
                      <span className="font-semibold text-primary">
                        {placements}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-light-black">Success rate</span>
                      <span className="font-semibold text-primary">
                        {successRate}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-light-black">Rating</span>
                      <span className="font-semibold text-primary">
                        {details?.rating != null ? `${details.rating}/5` : "—"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-light-black">Response time</span>
                      <span className="font-semibold text-primary">
                        {responseTimeValue}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="rounded-[22px] border border-[#CFEFDD] bg-[#EFFBF4] p-6">
                  <h3 className="text-lg font-semibold text-primary">
                    Why they may fit you
                  </h3>
                  <p className="mt-3 text-sm leading-7 text-light-black">
                    {whyTheyFitText}
                  </p>
                </div>

                <button
                  onClick={handleSelectAsPartner}
                  disabled={submitting}
                  className="rounded-full bg-[#2257FF] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#1B46CB] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? "Selecting..." : "Select as referral partner"}
                </button>

                <button
                  type="button"
                  className="rounded-full border border-[#D7DFEA] bg-white px-5 py-3 text-sm font-semibold text-primary transition hover:bg-[#F6F8FC]"
                >
                  Message first
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReferralPartnerDetailsPopup;
